import MarkdownIt from "markdown-it";
import { parseExcalidrawBlocks } from "./extensions";

// Initialize markdown-it parser (same parser used by tiptap-markdown internally)
const md = new MarkdownIt({
    html: true, // Enable HTML tags in source
    linkify: true, // Auto-convert URL-like text to links
    breaks: true // Convert '\n' in paragraphs into <br>
});

// Enable GFM table support and ensure fence (```) support is enabled
// See: https://github.com/markdown-it/markdown-it#syntax-extensions
md.enable(["table", "fence"]);

/**
 * Process HTML to sanitize malformed structures
 *
 * Fixes malformed code blocks and other HTML issues
 */
function processHTML(html: string): string {
    console.log("[TiptapEditor] processHTML called, HTML length:", html.length);
    console.log("[TiptapEditor] HTML contains <svg>?", html.includes("<svg"));

    // Step 0: Normalize whitespace to prevent tabs from being interpreted as code blocks
    // Remove leading tabs/spaces from each line while preserving intentional indentation
    const normalizedHTML = html
        .split("\n")
        .map(line => line.trimStart()) // Remove leading whitespace
        .join("\n")
        .replace(/\n\n+/g, "\n\n"); // Collapse multiple blank lines

    console.log("[TiptapEditor] After whitespace normalization, length:", normalizedHTML.length);

    const parser = new DOMParser();
    const doc = parser.parseFromString(normalizedHTML, "text/html");

    // CRITICAL: Wrap SVG elements BEFORE TipTap parses them
    // ProseMirror strips nested SVG children (defs, gradients, etc.) because they're not in the schema
    // We must preserve the SVG by wrapping it in a data attribute that TipTap will recognize
    const svgElements = doc.querySelectorAll("svg");
    svgElements.forEach(svgElement => {
        const svgHTML = svgElement.outerHTML;
        console.log("[TiptapEditor] Wrapping SVG for preservation, length:", svgHTML.length);

        // CRITICAL: Base64 encode SVG to prevent corruption during HTML attribute storage
        // HTML attributes escape special characters, which corrupts complex SVG structures
        const encodedSVG = btoa(unescape(encodeURIComponent(svgHTML)));
        console.log("[TiptapEditor] Encoded SVG length:", encodedSVG.length);

        // Create wrapper div with base64-encoded data attribute
        const wrapper = doc.createElement("div");
        wrapper.setAttribute("data-svg-passthrough", "");
        wrapper.setAttribute("data-svg-html", encodedSVG);

        // Replace SVG with wrapper
        svgElement.replaceWith(wrapper);
    });

    // Sanitize malformed code blocks

    const codeElements = doc.querySelectorAll("pre code");

    codeElements.forEach(codeElement => {
        // Check if code element contains HTML elements (which shouldn't happen)
        const hasChildElements = codeElement.querySelector("*") !== null;

        if (hasChildElements || codeElement.textContent?.includes("```mermaid")) {
            console.warn("[TiptapEditor] Found malformed code block, extracting content...");

            // Get the parent pre element
            const preElement = codeElement.parentElement;
            if (!preElement) return;

            // Get all content (both text and HTML elements)
            const textContent = codeElement.textContent || "";

            // Extract all mermaid diagrams using regex
            const mermaidRegex = /```mermaid\s+([\s\S]*?)```/g;
            const mermaidMatches = [...textContent.matchAll(mermaidRegex)];

            if (mermaidMatches.length > 0) {
                console.log(`[TiptapEditor] Found ${mermaidMatches.length} mermaid diagram(s) in malformed code block`);

                // Create a container for all extracted content
                const container = doc.createElement("div");

                // Add each mermaid diagram as a proper div
                mermaidMatches.forEach(match => {
                    const mermaidCode = match[1].trim();

                    // Base64 encode the code to prevent HTML attribute normalization from stripping newlines
                    const encodedCode = btoa(unescape(encodeURIComponent(mermaidCode)));

                    const mermaidDiv = doc.createElement("div");
                    mermaidDiv.setAttribute("data-mermaid", "");
                    mermaidDiv.setAttribute("data-code", encodedCode);
                    mermaidDiv.setAttribute("data-code-encoding", "base64");
                    mermaidDiv.setAttribute("data-width", "700");
                    mermaidDiv.setAttribute("data-height", "525");
                    mermaidDiv.setAttribute("data-align", "left");

                    container.appendChild(mermaidDiv);
                });

                // Also extract any HTML elements that were trapped in the code block
                const childElements = Array.from(codeElement.children);
                childElements.forEach(child => {
                    // Clone and append to container
                    container.appendChild(child.cloneNode(true));
                });

                // Replace the pre element with the container's children
                const parent = preElement.parentNode;
                if (parent) {
                    Array.from(container.children).forEach(child => {
                        parent.insertBefore(child, preElement);
                    });
                    parent.removeChild(preElement);
                }
            }
        }
    });

    return doc.body.innerHTML;
}

/**
 * Parse content as either HTML or Markdown
 *
 * - If content starts with HTML tags, treat as HTML (preserves rich formatting)
 * - Otherwise, parse as Markdown (for LLM-generated content)
 */
export function parseContent(content: string): string {
    if (!content || content.trim() === "") {
        return "<p></p>";
    }

    let trimmed = content.trim();

    // Check if content is HTML-escaped (stored as &lt;div&gt; instead of <div>)
    const isHTMLEscaped = /^&lt;[a-z]/i.test(trimmed);
    if (isHTMLEscaped) {
        console.log("[TiptapEditor] Content is HTML-escaped, decoding...");
        // Decode HTML entities
        const textarea = document.createElement("textarea");
        textarea.innerHTML = trimmed;
        trimmed = textarea.value;
        console.log("[TiptapEditor] After decoding, first 200 chars:", trimmed.substring(0, 200));
    }

    // More robust HTML detection:
    // 1. Check if it starts with an HTML tag (any tag, not just specific ones)
    // 2. Check if it contains HTML-specific patterns (attributes, closing tags, etc.)
    const startsWithHTMLTag = /^<[a-z][\s\S]*>/i.test(trimmed);
    const containsHTMLAttributes = /\s(?:class|id|style|data-\w+)=["']/.test(trimmed);
    const containsClosingTags = /<\/[a-z]+>/i.test(trimmed);

    // If any of these conditions are met, treat as HTML
    const isHTML = startsWithHTMLTag || (containsHTMLAttributes && containsClosingTags);

    if (isHTML) {
        console.log("[TiptapEditor] Loading as HTML (preserves rich formatting)");
        console.log("[TiptapEditor] First 200 chars of HTML:", trimmed.substring(0, 200));

        // Check if content contains Excalidraw markers that need parsing
        const hasExcalidrawMarkers = trimmed.includes("EXCALIDRAW_START") || trimmed.includes("EXCALIDRAW_ERROR");
        if (hasExcalidrawMarkers) {
            console.log("[TiptapEditor] HTML contains Excalidraw markers, parsing...");
            trimmed = parseExcalidrawBlocks(trimmed);
        }

        // Only process HTML if it contains raw SVG tags or malformed code blocks
        // If it already has data-svg wrappers (from previous save), don't re-process to avoid corruption
        const needsProcessing = trimmed.includes("<svg") || trimmed.includes("```mermaid");

        if (needsProcessing) {
            console.log("[TiptapEditor] HTML needs processing (contains raw SVG or mermaid)");
            // Process HTML: convert SVG tags and sanitize in a single pass
            const processed = processHTML(trimmed);

            // Log a sample of the final HTML around any data-svg attributes
            if (processed.includes("data-svg")) {
                const index = processed.indexOf("data-svg");
                console.log("[TiptapEditor] Final HTML contains data-svg, sample:", processed.substring(Math.max(0, index - 100), index + 300));
            }

            return processed;
        } else {
            console.log("[TiptapEditor] HTML already processed, returning as-is");

            // Debug: Check if HTML contains data-svg and log a sample
            if (trimmed.includes("data-svg")) {
                const index = trimmed.indexOf("data-svg");
                console.log("[TiptapEditor] HTML contains data-svg wrapper, sample:", trimmed.substring(Math.max(0, index - 50), index + 500));

                // Check if the data-svg-code attribute is intact
                const codeMatch = trimmed.match(/data-svg-code="([^"]+)"/);
                if (codeMatch) {
                    console.log("[TiptapEditor] data-svg-code length:", codeMatch[1].length);
                    console.log("[TiptapEditor] data-svg-code first 200 chars:", codeMatch[1].substring(0, 200));
                } else {
                    console.error("[TiptapEditor] ⚠️ data-svg found but data-svg-code attribute is missing or malformed!");
                }
            }

            return trimmed;
        }
    }

    // Content is Markdown - parse it
    try {
        console.log("[TiptapEditor] Loading as Markdown from LLM");
        console.log("[TiptapEditor] First 200 chars of Markdown:", trimmed.substring(0, 200));

        // First, parse Excalidraw blocks (before markdown parsing to preserve the markers)
        const contentWithExcalidraw = parseExcalidrawBlocks(trimmed);
        console.log("[Excalidraw Debug] Does content contain Excalidraw?", trimmed.includes("EXCALIDRAW_START"));

        const html = md.render(contentWithExcalidraw);

        console.log("[Mermaid Debug] Does markdown contain mermaid?", trimmed.includes("```mermaid"));
        console.log("[Mermaid Debug] Does HTML contain language-mermaid?", html.includes("language-mermaid"));
        console.log("[Excalidraw Debug] Does HTML contain data-excalidraw?", html.includes("data-excalidraw"));

        // markdown-it creates: <pre><code class="language-mermaid">...</code></pre>
        // The Mermaid extension with HIGH PRIORITY (100) will parse it directly

        return html;
    } catch (error) {
        console.error("Error parsing markdown:", error);
        return `<p>${trimmed}</p>`;
    }
}
